import {areSomeOpen, debug} from '@wix/document-manager-core'
import type {Experiment, DSConfig} from '@wix/document-services-types'
import {getDsConfig} from './configs/getDsConfig'
import type {BootstrapConfig} from './types'
import _ from 'lodash'

const log = debug('csave')

export const editorX = 'editor_x'
export const wem = 'wem'
export const classic = 'Editor1.4'
export const editor3 = 'odeditor'
export const responsiveBlocks = 'responsiveBlocks'
export const presetPreview = 'preset_preview'
export const appBuilder = 'appBuilder'
export const expertsFeedback = 'experts_feedback'
export const logoMaker = 'Logo'
export const studio = 'studio'
export const DMMigratorX = 'DMMigratorX'
export const optimusOrigin = 'DMMigrator'
export const testOrigin = 'initialize.spec'
export const dsIntergrationOrigin = 'DSintegrationTestOrigin'
export const dsIntergrationOriginResponsive = 'DSintegrationTestOriginResponsive'

const ALWAYS_OPEN = true
const ALWAYS_CLOSED = false

type OriginStatus = string[] | boolean

const originBasedExperiments: Record<string, Record<string, OriginStatus>> = {
    acceptTransactionsFromDuplexer: {
        [editorX]: ALWAYS_OPEN,
        [classic]: ALWAYS_OPEN,
        [wem]: ALWAYS_OPEN,
        [responsiveBlocks]: ALWAYS_OPEN,
        [studio]: ALWAYS_OPEN,
        [editor3]: ALWAYS_OPEN,
        [testOrigin]: ALWAYS_OPEN,
        [dsIntergrationOrigin]: ALWAYS_OPEN,
        [dsIntergrationOriginResponsive]: ALWAYS_OPEN
    },
    cedit: {
        [editorX]: ALWAYS_OPEN,
        [classic]: ['dm_ceditSingleUserForClassic'],
        [wem]: ['dm_ceditSingleUserForClassic'],
        [responsiveBlocks]: ALWAYS_OPEN,
        [studio]: ALWAYS_OPEN,
        [editor3]: ['dm_ceditForEditor3'],
        [testOrigin]: ALWAYS_OPEN,
        [dsIntergrationOrigin]: ['dm_ceditSingleUserForClassic'],
        [dsIntergrationOriginResponsive]: ALWAYS_OPEN,
        [optimusOrigin]: ALWAYS_CLOSED
    },
    createRevision: {
        [classic]: ['dm_csaveCreateRevision'],
        [wem]: ['dm_csaveCreateRevision'],
        [editorX]: ALWAYS_OPEN,
        [studio]: ALWAYS_OPEN,
        [editor3]: ALWAYS_OPEN,
        [testOrigin]: ALWAYS_OPEN,
        [DMMigratorX]: ALWAYS_OPEN,
        [optimusOrigin]: ALWAYS_OPEN
    },
    immutableDomain: {
        [classic]: ALWAYS_OPEN,
        [wem]: ALWAYS_OPEN,
        [editorX]: ALWAYS_OPEN,
        [responsiveBlocks]: ALWAYS_OPEN,
        [presetPreview]: ALWAYS_OPEN,
        [logoMaker]: ['specs.logo-builder.client.ImmutableDomain'],
        [expertsFeedback]: ALWAYS_OPEN,
        [appBuilder]: ALWAYS_OPEN,
        [testOrigin]: ALWAYS_OPEN,
        [studio]: ALWAYS_OPEN,
        [editor3]: ALWAYS_OPEN
    },
    concurrentWorkEnabled: {
        [editorX]: ALWAYS_OPEN,
        [studio]: ALWAYS_OPEN,
        [responsiveBlocks]: ALWAYS_OPEN,
        [testOrigin]: ALWAYS_OPEN,
        [dsIntergrationOriginResponsive]: ALWAYS_OPEN,
        [editor3]: ALWAYS_OPEN
    }
}

export const isOpenForOrigin = (experiment: Experiment, names: string[], origin: string, origins: string[]) =>
    areSomeOpen(experiment, names) && origins.includes(origin)

const isOpenByOrigin = (origin: string, experiment: Experiment, openByOrigin: Record<string, OriginStatus>) => {
    if (origin in openByOrigin) {
        const openBy = openByOrigin[origin]
        if (_.isBoolean(openBy)) {
            return openBy
        }
        return _.isEmpty(openBy) || areSomeOpen(experiment, openBy)
    }
    return false
}

export const isCEditOpen = (origin: string, experiment: Experiment) =>
    isOpenByOrigin(origin, experiment, originBasedExperiments.cedit)

export const isAcceptTransactionsFromDuplexerOpen = (origin: string, experiment: Experiment) =>
    isOpenByOrigin(origin, experiment, originBasedExperiments.acceptTransactionsFromDuplexer)
export const isCreateRevisionOpen = (origin: string, experiment: Experiment) =>
    isOpenByOrigin(origin, experiment, originBasedExperiments.createRevision)

export const isImmutableDomainOpen = (origin: string, experiment: Experiment) =>
    isOpenByOrigin(origin, experiment, originBasedExperiments.immutableDomain)

const isConcurrentWorkEnabled = (origin: string, experiment: Experiment) =>
    isOpenByOrigin(origin, experiment, originBasedExperiments.concurrentWorkEnabled)

export const isCSaveOpen = () => true

const isTrue = (s?: string | boolean) => s === true || s === 'true'

const stateString = (b?: boolean) => (b ? 'on' : 'off')

const readOnlyEndpoints = ['renderer/external_preview', 'renderer/revisions']

const isReadOnlyEndpoint = (pathname?: string) => readOnlyEndpoints.some(endpoint => pathname?.includes(endpoint))

export const createConfig = (config: BootstrapConfig, experimentInstance: Experiment): DSConfig => {
    const isReadOnly = isTrue(config.isReadOnly) || isReadOnlyEndpoint(config.pathname)
    const disableSave = isTrue(config.disableSave)
    const enableScopes = isTrue(config.enableScopes)
    const enableRepeatersInScopes = isTrue(config.enableRepeatersInScopes)
    const enableWidgetInRepeater = isTrue(config.enableWidgetInRepeater)
    const overrides: Partial<DSConfig> = {
        editorType: config.editorType,
        disableSave: disableSave || isReadOnly,
        enableScopes,
        enableRepeatersInScopes,
        cedit: isCEditOpen(config.dsOrigin, experimentInstance),
        acceptTransactionsFromDuplexer: isAcceptTransactionsFromDuplexerOpen(config.dsOrigin, experimentInstance),
        createRevision: isCreateRevisionOpen(config.dsOrigin, experimentInstance),
        immutableDomain: isImmutableDomainOpen(config.dsOrigin, experimentInstance),
        concurrentWorkEnabled: isConcurrentWorkEnabled(config.dsOrigin, experimentInstance),
        shouldSquashTransactions: config.shouldSquashTransactions,
        localEditorWsUrl: config.localEditorWsUrl,
        enableWidgetInRepeater
    }
    if (isReadOnly) {
        overrides.isReadOnly = true
    } else {
        delete config.isReadOnly //cannot set readonly to non-readonly, so if you passed false, we ignore it
    }

    config.disableAutoSave = isTrue(config.disableAutoSave)
    const disableAutoSave =
        isTrue(config.disableAutoSave) || (isTrue(config.isqa) && !isTrue(config.forceAutosave)) || isReadOnly

    if (disableAutoSave) {
        overrides.disableAutoSave = disableAutoSave
    }

    if (isTrue(config.showMobileView)) {
        overrides.showMobileView = true
    }

    if (_.isBoolean(config.excludeRevoking)) {
        overrides.excludeRevoking = config.excludeRevoking
    }

    const dsConf = getDsConfig(config, experimentInstance, overrides)

    //fix isReadOnly inherited flags. This whole function will be refactored in a later PR
    if (dsConf.isReadOnly) {
        dsConf.disableSave = true
        dsConf.disableAutoSave = true
    }

    if ((!isReadOnly && dsConf.continuousSave !== false) || config.untilTransactionId) {
        dsConf.continuousSave = true
    }
    log.info(`For origin ${config.dsOrigin} continuousSave is ${stateString(overrides.continuousSave)}`)
    log.info(`cedit is ${stateString(overrides.cedit)}`)
    log.info(`createRevision is ${stateString(overrides.createRevision)}`)
    log.info(`autosave is ${dsConf.disableAutoSave ? 'disabled' : 'enabled'}`)
    log.info(`scopes is ${dsConf.enableScopes ? 'disabled' : 'enabled'}`)
    log.info(`repeaters in scopes is ${dsConf.enableRepeatersInScopes ? 'disabled' : 'enabled'}`)
    log.info(`widget in repeater is ${dsConf.enableWidgetInRepeater ? 'enabled' : 'disabled'}`)
    return dsConf
}
